import { AlertTriangle, ArrowDownUp, CheckCircle2, Info, Loader2, Settings } from "lucide-react"

import type { SwapActionMode } from "../../swap-mode"
import { SwapRouteIconView } from "./swap-route-icon-view"

export type SwapTokenPanelDTO = {
  symbol: string
  name: string
  imageUrl: string
  balanceLabel: string
  maxAmountLabel: string
  valueLabel: string
}

export type SwapRouteDTO = {
  aggregatorId: string
  sourceLabel: string
  statusLabel: string
  reasonLabel: string
  outputAmount: string
  outputValueLabel: string
  priceImpactLabel: string
  hopLabel: string
  slippageLabel: string
  isFallback: boolean
  isUnsafe: boolean
}

export interface SwapCardContainerViewProps {
  inputToken: SwapTokenPanelDTO | undefined
  outputToken: SwapTokenPanelDTO | undefined
  inputAmount: string
  inputValueLabel: string
  onTypingChange: (amount: string) => void
  onBalanceClick: (amount: string) => void
  onPercentageButtonClick: (percentage: number) => void
  onInputTokenSelect: () => void
  onOutputTokenSelect: () => void
  onSwitchTokens: () => void
  actionMode: SwapActionMode
  route: SwapRouteDTO | undefined
  routeUnavailableLabel?: string
  isLoadingQuotes: boolean
  onRefreshQuotes: () => void
  isSwappingProcessActive: boolean
  isWalletConnected: boolean
  onSwapAction: () => void
  onSwapDone: () => void
  hasError?: boolean
  isSuccess?: boolean
  errorMessage?: string | null
  onSettingsClick: () => void
  slippageTolerance: string
}

export const SwapCardContainerView = ({
  inputToken,
  outputToken,
  inputAmount,
  inputValueLabel,
  onTypingChange,
  onBalanceClick,
  onPercentageButtonClick,
  onInputTokenSelect,
  onOutputTokenSelect,
  onSwitchTokens,
  actionMode,
  route,
  routeUnavailableLabel,
  isLoadingQuotes,
  onRefreshQuotes,
  isSwappingProcessActive,
  isWalletConnected,
  onSwapAction,
  onSwapDone,
  hasError = false,
  isSuccess = false,
  errorMessage,
  onSettingsClick,
  slippageTolerance,
}: SwapCardContainerViewProps) => {
  const hasInputAmount = inputAmount.length > 0 && Number(inputAmount) > 0
  const isWrapOrUnwrap = actionMode === "wrap" || actionMode === "unwrap"
  const walletRequired = hasInputAmount && !isWalletConnected && (isWrapOrUnwrap || Boolean(route))
  const canSwap =
    hasInputAmount &&
    isWalletConnected &&
    (isWrapOrUnwrap || Boolean(route)) &&
    !(isLoadingQuotes && !route) &&
    !route?.isUnsafe
  const ctaLabel = getCtaLabel({
    actionMode,
    hasInputAmount,
    isLoadingQuotes,
    isWalletConnected,
    route,
    routeUnavailableLabel,
  })
  const swapProgressCopy = getSwapProgressCopy(hasError, isSuccess, errorMessage)

  return (
    <div className="flex min-h-[620px] items-start justify-center px-3 pt-10">
      <div className="relative w-full max-w-[520px] overflow-hidden rounded-[2rem] border border-p400-light bg-p400 shadow-2xl shadow-black/20">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_hsl(var(--p100)/0.18),_transparent_35%)]" />
        <div className="relative flex items-start justify-between p-6 pb-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary-300">Swap</p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-t100">Best routes, clear execution.</h1>
          </div>
          <button
            onClick={onSettingsClick}
            className="rounded-full border border-p300-light bg-p300 p-2 text-t200 transition-colors hover:bg-p300-light hover:text-t100"
            aria-label="Open swap settings"
          >
            <Settings className="h-5 w-5" />
          </button>
        </div>

        <div className="relative flex flex-col gap-3 p-4">
          {isSwappingProcessActive ? (
            <div className="rounded-3xl border border-p300-light bg-p300 p-5">
              <div className="flex items-center gap-3">
                {isSuccess ? (
                  <div className="rounded-2xl bg-success-background/20 p-2 text-success-text">
                    <CheckCircle2 className="h-7 w-7" />
                  </div>
                ) : hasError ? (
                  <AlertTriangle className="h-6 w-6 text-error-text" />
                ) : (
                  <Loader2 className="h-6 w-6 animate-spin text-primary-300" />
                )}
                <div>
                  <p className="text-lg font-bold text-t100">{swapProgressCopy.title}</p>
                  <p className="text-sm text-t200">
                    {swapProgressCopy.description}
                  </p>
                </div>
              </div>
              {(hasError || isSuccess) && (
                <button
                  onClick={onSwapDone}
                  className={`mt-5 w-full rounded-2xl py-3 font-bold text-t100 transition-colors ${
                    isSuccess ? "bg-success-background hover:brightness-110" : "bg-p200 hover:bg-p200-light"
                  }`}
                >
                  Back to swap
                </button>
              )}
            </div>
          ) : (
            <>
              <TokenPanel
                label="Sell"
                token={inputToken}
                amount={inputAmount}
                valueLabel={inputValueLabel}
                onTokenSelect={onInputTokenSelect}
                onTypingChange={onTypingChange}
                footer={
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs text-t200">Balance {inputToken?.balanceLabel ?? "0"}</span>
                    <div className="flex gap-1">
                      {[25, 50].map((percentage) => (
                        <button
                          key={percentage}
                          onClick={() => onPercentageButtonClick(percentage)}
                          className="rounded-lg bg-p300 px-2 py-1 text-xs font-bold text-t200 hover:bg-p300-light hover:text-t100"
                        >
                          {percentage}%
                        </button>
                      ))}
                      <button
                        onClick={() => onBalanceClick(inputToken?.maxAmountLabel ?? "0")}
                        className="rounded-lg bg-p300 px-2 py-1 text-xs font-bold text-t200 hover:bg-p300-light hover:text-t100"
                      >
                        Max
                      </button>
                    </div>
                  </div>
                }
              />

              <div className="relative -my-1 flex justify-center">
                <button
                  onClick={onSwitchTokens}
                  className="rounded-2xl border-4 border-p400 bg-p300 p-2.5 text-t200 shadow-xl transition-all hover:scale-105 hover:bg-p300-light hover:text-primary-300 active:scale-95"
                  aria-label="Switch input and output tokens"
                >
                  <ArrowDownUp className="h-5 w-5" />
                </button>
              </div>

              <TokenPanel
                label="Buy"
                token={outputToken}
                amount={route?.outputAmount ?? (isWrapOrUnwrap ? inputAmount : "")}
                valueLabel={route?.outputValueLabel ?? "$0.00"}
                onTokenSelect={onOutputTokenSelect}
                readOnly
                footer={<span className="text-xs text-t200">Balance {outputToken?.balanceLabel ?? "0"}</span>}
              />

              <div className="rounded-3xl border border-p300-light bg-p300 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl ${
                        route?.isUnsafe ? "bg-error-background/15 text-error-text" : "bg-p200 text-primary-300"
                      }`}
                    >
                      {route?.isUnsafe ? (
                        <Info className="block h-5 w-5 shrink-0" />
                      ) : (
                        <SwapRouteIconView aggregatorId={route?.aggregatorId} className="block h-5 w-5 shrink-0" />
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-t100">
                    {isLoadingQuotes ? "Getting quote…" : route?.statusLabel ?? "Ready when you are"}
                      </p>
                      <p className="mt-1 text-sm text-t200">
                        {isWrapOrUnwrap
                          ? "Native wrap route at 1:1."
                          : route?.reasonLabel ?? routeUnavailableLabel ?? "Enter an amount to quote."}
                      </p>
                    </div>
                  </div>
                  {isLoadingQuotes ? (
                    <Loader2 className="h-5 w-5 animate-spin text-primary-300" />
                  ) : (
                    route && <CheckCircle2 className="h-5 w-5 text-primary-300" />
                  )}
                </div>

                {route && (
                  <details className="mt-4 rounded-2xl border border-p400-light bg-p400 p-3">
                    <summary className="cursor-pointer text-sm font-bold text-t100">Quote details</summary>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <RouteMetric label="Route" value={route.sourceLabel} />
                      <RouteMetric label="Hops" value={route.hopLabel} />
                      <RouteMetric label="Impact" value={route.priceImpactLabel} />
                      <RouteMetric label="Slippage" value={route.slippageLabel} />
                    </div>
                  </details>
                )}
              </div>

              <button
                onClick={onSwapAction}
                disabled={!canSwap}
                title={walletRequired ? "Connect your wallet to continue." : undefined}
                className={`flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-lg font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  canSwap
                    ? "bg-primary text-t100 shadow-lg hover:bg-primary-light"
                    : "cursor-not-allowed bg-p300 text-t300"
                }`}
              >
                {isLoadingQuotes && !route && <Loader2 className="h-5 w-5 animate-spin" />}
                {ctaLabel}
              </button>
              <button onClick={onRefreshQuotes} className="text-center text-xs font-bold text-t200 hover:text-t100">
                Refresh route
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const TokenPanel = ({
  label,
  token,
  amount,
  valueLabel,
  onTokenSelect,
  onTypingChange,
  readOnly = false,
  footer,
}: {
  label: string
  token: SwapTokenPanelDTO | undefined
  amount: string
  valueLabel: string
  onTokenSelect: () => void
  onTypingChange?: (amount: string) => void
  readOnly?: boolean
  footer: React.ReactNode
}) => {
  return (
    <section className="rounded-3xl border border-p300-light bg-p300 p-4 focus-within:border-p100">
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm font-bold text-t100">{label}</span>
        {footer}
      </div>
      <div className="flex items-start justify-between gap-4">
        <button
          onClick={onTokenSelect}
          className="flex shrink-0 items-center gap-2 rounded-2xl border border-p200-light bg-p200 px-3 py-2 transition-colors hover:bg-p200-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {token?.imageUrl ? (
            <img src={token.imageUrl} alt={token.symbol} className="h-7 w-7 rounded-full" />
          ) : (
            <div className="h-7 w-7 rounded-full bg-p100" />
          )}
          <span className="font-bold text-t100">{token?.symbol ?? "Select"}</span>
        </button>
        <div className="min-w-0 flex-1 text-right">
          <input
            value={amount}
            readOnly={readOnly}
            inputMode="decimal"
            placeholder="0"
            onChange={(event) => {
              const value = event.target.value
              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                onTypingChange?.(value)
              }
            }}
            className="w-full bg-transparent text-right text-4xl font-bold tracking-tight text-t100 outline-none placeholder:text-t300"
          />
          <p className="mt-1 truncate text-sm text-t200">{valueLabel}</p>
        </div>
      </div>
    </section>
  )
}

const RouteMetric = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="rounded-xl bg-p300 p-2">
      <p className="text-xs text-t300">{label}</p>
      <p className="truncate text-sm font-bold text-t100">{value}</p>
    </div>
  )
}

const getCtaLabel = ({
  actionMode,
  hasInputAmount,
  isLoadingQuotes,
  isWalletConnected,
  route,
  routeUnavailableLabel,
}: {
  actionMode: SwapActionMode
  hasInputAmount: boolean
  isLoadingQuotes: boolean
  isWalletConnected: boolean
  route: SwapRouteDTO | undefined
  routeUnavailableLabel?: string
}): string => {
  if (!hasInputAmount) return "Enter amount"
  if (!isWalletConnected && (actionMode === "wrap" || actionMode === "unwrap" || route)) return "Connect wallet to swap"
  if (actionMode === "wrap") return "Wrap ETH"
  if (actionMode === "unwrap") return "Unwrap WETH"
  if (isLoadingQuotes && !route) return "Getting quote"
  if (route?.isUnsafe) return "Route blocked for safety"
  if (route) return "Swap"
  return routeUnavailableLabel ? "Quote unavailable" : "No quote available"
}

const getSwapProgressCopy = (
  hasError: boolean,
  isSuccess: boolean,
  errorMessage?: string | null,
): { title: string; description: string } => {
  if (isSuccess) {
    return {
      title: "Swap complete",
      description: "Your trade has settled.",
    }
  }

  if (!hasError) {
    return {
      title: "Swap in progress",
      description: "Confirm in your wallet and we will handle the rest.",
    }
  }

  const normalizedError = (errorMessage ?? "").toLowerCase()
  if (normalizedError.includes("no wallet connected")) {
    return {
      title: "Connect wallet",
      description: "Connect your wallet to review and submit this swap.",
    }
  }
  if (normalizedError.includes("user rejected") || normalizedError.includes("rejected the request")) {
    return {
      title: "Request cancelled",
      description: "No transaction was submitted. You can try again when ready.",
    }
  }
  if (normalizedError.includes("no quote selected")) {
    return {
      title: "Refresh quote",
      description: "The quote is no longer active. Refresh the route and try again.",
    }
  }

  return {
    title: "Swap could not be completed",
    description: "Please refresh the quote and try again.",
  }
}
